import { flash, SubmitButton } from "@xola/ui-kit";
import { omit } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import { CheckInPreference, CheckInValidationCount, CheckInValidationType, Experience } from "types";
import { object } from "yup";
import { api } from "../../../api/api";
import { ScrollToFieldError } from "../../../helpers/validationHelper";
import { useSeller } from "../../../hooks/useAccount";
import { useAutoFetchCollection } from "../../../hooks/useAutoFetchCollection";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { ContentLayout } from "../../Layout/ContentLayout";
import { Loader } from "../../Loader";
import { NoOverrides, OverridingExperiencesList } from "./OverridingExperiencesList";
import { CheckInPreferenceFormValues } from "@/components/Product/ExperienceCheckIn/ExperienceCheckIn.types";
import { validationSchema } from "@/components/Product/ExperienceCheckIn/MultipleCheckInField.helpers";
import { CheckInConfigurationSection } from "@/components/Product/GlobalSettings/CheckInPreference/CheckInConfigurationSection";

export const CheckInPreferences = () => {
    const seller = useSeller();
    const invalidate = useInvalidate();

    const { mutateAsync: updateCheckInPreference, isLoading: isLoadingCheckInPreference } = useMutation(
        async (payload: CheckInPreference) => {
            return api.post(`/sellers/${seller.id}/preferences/checkIn`, payload);
        },
    );

    const { data: experiences, isLoading: isLoadingExperiences } = useAutoFetchCollection<Experience>("experiences");
    const overridingExperiences = experiences.filter((experience: Experience) => experience.checkInPreference);

    const handleSubmit = async (payload: CheckInPreferenceFormValues) => {
        payload.validations = payload.enabled
            ? /** As of now, we allow only single check-in preference at seller level. As, scheduled experiences multi check-in feature is not introduced yet. Once, that is done, remove this `if` block */
              [{ object: CheckInValidationType.COUNT, max: 1 }]
            : /** When user disables the check-in preference, we need to also remove all the attached validations */
              [];

        await updateCheckInPreference(omit(payload, ["multiCheckInEnabled", "checkInWindowEnabled"]));
        flash.success("Successfully updated preferences");
        invalidate("sellers", seller.id);
    };

    const isLoading = isLoadingExperiences || isLoadingCheckInPreference;

    return (
        <ContentLayout isWide isDisabled={isLoading}>
            <XolaFormik
                validateOnChange={false}
                validationSchema={getValidationSchema()}
                initialValues={getInitialValues(seller.preferences.checkIn)}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, resetForm }) => {
                    const handleReset = () => {
                        resetForm({ values: getInitialValues(seller.preferences.checkIn) });
                    };

                    return (
                        <>
                            <h2 className="mt-4">Check-In Preferences</h2>
                            <div className="divide-y divide-gray-light">
                                <CheckInConfigurationSection />

                                <ContentLayout.Section
                                    title="Overrides"
                                    description="Products that use their own check-in settings are displayed here"
                                >
                                    {isLoading ? (
                                        <Loader size="tiny" className="p-2" text="Loading Experiences" />
                                    ) : (
                                        <div>
                                            {overridingExperiences.length > 0 ? (
                                                <OverridingExperiencesList experiences={overridingExperiences} />
                                            ) : (
                                                <NoOverrides />
                                            )}
                                        </div>
                                    )}
                                </ContentLayout.Section>

                                <ContentLayout.Footer preferenceSidebar>
                                    <SubmitButton
                                        isLoading={isLoading}
                                        type="submit"
                                        size="medium"
                                        onClick={handleSubmit}
                                    >
                                        Save
                                    </SubmitButton>
                                    <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
                                </ContentLayout.Footer>

                                <ScrollToFieldError />
                            </div>
                        </>
                    );
                }}
            </XolaFormik>
        </ContentLayout>
    );
};

const getInitialValues = (checkInPreference: CheckInPreference): CheckInPreferenceFormValues => {
    const countValidation = checkInPreference?.validations?.find(
        (validation) => validation.object === CheckInValidationType.COUNT,
    ) as CheckInValidationCount | undefined;

    const enabled = checkInPreference?.enabled ?? false;

    return {
        checkInWindowEnabled: false,
        enabled: checkInPreference?.enabled ?? false,
        validations: checkInPreference.validations ?? [],
        multiCheckInEnabled: countValidation ? countValidation.max > 1 : enabled,
    };
};

const getValidationSchema = () => {
    return object().shape(validationSchema);
};
